import { ForceableAuthModalView, FORCEABLE_AUTH_MODAL_VIEWS } from './types';
import { currentPathEquals } from '../../../../utils/localization';

export const AUTH_MODAL_VIEW_QUERY_PARAM = 'authModalView';

const LEGACY_QUERY_FLAG_TO_VIEW: Record<string, ForceableAuthModalView> = {
  signIn: 'sign-in',
  startNow: 'create-account',
  recover: 'recovery',
  forgotPassword: 'recovery',
};

const LEGACY_HASH_TO_VIEW: Record<string, ForceableAuthModalView> = {
  '#signIn': 'sign-in',
  '#startNow': 'create-account',
  '#forgotPassword': 'recovery',
  '#forgot-password': 'recovery',
};

const AUTH_PAGE_ONLY_HASH_TO_VIEW: Record<string, ForceableAuthModalView> = {
  '#recover': 'recovery',
};

const DISABLED_URL_FLAG_VALUES = new Set(['0', 'false', 'no', 'off']);

export type ForcedAuthModalResolution = {
  view: ForceableAuthModalView;
  source: 'canonical-query-param' | 'legacy-query-flag' | 'legacy-hash';
  matchedKey: string;
};

type ForcedAuthModalResolutionOptions = {
  includeAuthPageOnlyHashes?: boolean;
};

export const isForceableAuthModalView = (value: string): value is ForceableAuthModalView => {
  return FORCEABLE_AUTH_MODAL_VIEWS.includes(value as ForceableAuthModalView);
};

export function isAccountLoginPage(): boolean {
  return currentPathEquals('/account/login');
}

const normalizeAuthModalView = (value: string | null): ForceableAuthModalView | null => {
  if (!value) return null;

  const normalizedValue = value
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-');

  switch (normalizedValue) {
    case 'sign-in':
    case 'signin':
      return 'sign-in';
    case 'create-account':
    case 'createaccount':
    case 'start-now':
    case 'startnow':
      return 'create-account';
    case 'recovery':
    case 'recover':
    case 'forgot-password':
    case 'forgotpassword':
      return 'recovery';
    default:
      return null;
  }
};

const isEnabledUrlFlag = (searchParams: URLSearchParams, key: string) => {
  if (!searchParams.has(key)) return false;

  const value = searchParams.get(key);
  if (value === null || value === '') return true;

  return !DISABLED_URL_FLAG_VALUES.has(value.trim().toLowerCase());
};

function resolveForcedAuthView(
  location: Location = window.location,
  options: ForcedAuthModalResolutionOptions = {}
): ForcedAuthModalResolution | null {
  const searchParams = new URLSearchParams(location.search);
  const canonicalView = normalizeAuthModalView(searchParams.get(AUTH_MODAL_VIEW_QUERY_PARAM));

  if (canonicalView && isForceableAuthModalView(canonicalView)) {
    return {
      view: canonicalView,
      source: 'canonical-query-param',
      matchedKey: AUTH_MODAL_VIEW_QUERY_PARAM,
    };
  }

  for (const [key, view] of Object.entries(LEGACY_QUERY_FLAG_TO_VIEW)) {
    if (isEnabledUrlFlag(searchParams, key)) {
      return {
        view,
        source: 'legacy-query-flag',
        matchedKey: key,
      };
    }
  }

  const legacyHashToView = options.includeAuthPageOnlyHashes
    ? { ...LEGACY_HASH_TO_VIEW, ...AUTH_PAGE_ONLY_HASH_TO_VIEW }
    : LEGACY_HASH_TO_VIEW;

  const legacyHashView = legacyHashToView[location.hash];
  if (legacyHashView) {
    return {
      view: legacyHashView,
      source: 'legacy-hash',
      matchedKey: location.hash,
    };
  }

  return null;
}

export function resolveForcedAuthModalView(location: Location = window.location): ForcedAuthModalResolution | null {
  return resolveForcedAuthView(location);
}

export function resolveForcedAuthPageView(location: Location = window.location): ForcedAuthModalResolution | null {
  return resolveForcedAuthView(location, { includeAuthPageOnlyHashes: true });
}

export const getAuthModalReturnUrl = (location: Location = window.location) => {
  const url = new URL(location.href);
  url.searchParams.delete(AUTH_MODAL_VIEW_QUERY_PARAM);

  Object.keys(LEGACY_QUERY_FLAG_TO_VIEW).forEach((key) => {
    url.searchParams.delete(key);
  });

  if (url.hash in LEGACY_HASH_TO_VIEW) {
    url.hash = '';
  }

  return `${url.pathname}${url.search}${url.hash}`;
};
